import { createFetcher } from '@repo/api-client';
import { getAccessToken, getRefreshToken, getActiveOrgUnitId, updateTokens } from './session';
import { emitLogout } from './query-client';
import { reportWebError } from './error-reporter';
import { requestTwoFactorStepUp } from './two-factor-step-up';

export function resolveApiBaseUrl(envUrl = import.meta.env.VITE_API_URL, isDev = import.meta.env.DEV): string {
  if (isDev) {
    return '/network/api';
  }
  const raw = (envUrl || '').trim().replace(/\/+$/, '');
  if (!raw || raw === '/api') {
    return '/api';
  }
  if (raw.endsWith('/api')) {
    return raw;
  }
  return `${raw}/api`;
}

export function resolveApiRoot(envUrl = import.meta.env.VITE_API_URL, isDev = import.meta.env.DEV): string {
  if (isDev) {
    return '/network';
  }
  const raw = (envUrl || '').trim().replace(/\/+$/, '');
  if (!raw || raw === '/api') {
    return '';
  }
  if (raw.endsWith('/api')) {
    return raw.slice(0, -4);
  }
  return raw;
}

export const fetcherConfig = {
  baseUrl: resolveApiBaseUrl(),
  getToken: getAccessToken,
  getRefreshToken: getRefreshToken,
  getOrgUnitId: getActiveOrgUnitId,
  getClientContext: () => 'staff' as const,
  onTokensRefreshed: updateTokens,
  onLogout: emitLogout,
  onError: reportWebError,
  // RG-001: routes guarded by @RequireTwoFactor() (or MANDATORY_2FA_ROLES) answer 401 with
  // TWO_FACTOR_STEP_UP_REQUIRED. Open the code dialog instead of treating it as a dead session;
  // the fetcher replays the request once verification succeeds.
  onTwoFactorStepUp: requestTwoFactorStepUp,
  // Phase 1: prefer httpOnly cookie (auto-sent by browser) over Bearer token.
  // Bearer token from localStorage is kept as fallback for backward compatibility.
  // Phase 2: remove this flag and the getToken/getRefreshToken/onTokensRefreshed config.
  cookieFirst: false,
};

export const fetcher = createFetcher(fetcherConfig);

// For endpoints deliberately excluded from the API's `/api` global prefix
// (e.g. /health, /public/branding — see apps/api/src/main.ts's
// setGlobalPrefix exclude list), reusing `fetcher`'s auth/refresh handling
// but without the `/api` segment.
export const bareFetcher = createFetcher({ ...fetcherConfig, baseUrl: resolveApiRoot() });
